import { useEffect, useState } from 'react';
import {
  listPoolOffers,
  type ApiClient,
  type AvailableCategory,
  type PoolOfferListItem,
} from '../../../modules/api';

export interface CategoryOffersState {
  offers: PoolOfferListItem[];
  loading: boolean;
  error: string | null;
}

const EMPTY: PoolOfferListItem[] = [];

/**
 * Loads every pack in the given category — drives the modern Slider's
 * peek slots. Refetches whenever the user switches sport in the header.
 * The `available_for_operator(operator)` scope is applied server-side, so
 * disabled packs never reach the client.
 *
 * `category` is the matching `AvailableCategory` (use `null` while the
 * categories list is still loading — the hook will idle).
 */
export function useCategoryOffers(
  client: ApiClient,
  category: AvailableCategory | null,
): CategoryOffersState {
  const [offers, setOffers] = useState<PoolOfferListItem[]>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categoryId = category?.categoryId ?? null;
  const categoryClassName = category?.categoryClassName ?? null;

  useEffect(() => {
    if (categoryId === null || categoryId === 0) {
      setOffers(EMPTY);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    listPoolOffers(client, {
      categoryId,
      ...(categoryClassName ? { categoryClassName } : {}),
      perPage: 50,
    })
      .then((list) => {
        if (cancelled) return;
        setOffers(list);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load category offers');
        setOffers(EMPTY);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, categoryId, categoryClassName]);

  return { offers, loading, error };
}
